import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { HiCheck, HiOutlineClock, HiX } from 'react-icons/hi';
import { BackButton, Button, Modal } from '@src/components';
import IconBulletPointItem from '@src/components/IconBulletPointItem';
import NumberIcon from '@src/components/NumberIcon';
import { useCreateOrderVoucherCheckoutUrlMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import { getRewardful } from '@src/services/rewardful';
import OrderVoucherIcon from '../components/OrderVoucherIcon';

function PlaceNifOrderPage(): JSX.Element {
  const { t, i18n } = useTranslation(['nif']);
  const [showAdditionalRequirementsModal, setShowAdditionalRequirementsModal] =
    useState(false);
  const [createCheckoutUrl] = useCreateOrderVoucherCheckoutUrlMutation();
  const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
  const toast = useToast();

  const redirectToCheckout = useCallback(async () => {
    try {
      setIsRedirectingToCheckout(true);

      const rewardfulObj = getRewardful();
      const { data } = await createCheckoutUrl({
        variables: {
          input: {
            rewardfulReferralId: rewardfulObj?.referral,
            couponId: rewardfulObj?.coupon?.id,
            voucherType: 'NIF',
            locale: i18n.language,
          },
        },
      });
      if (!data) {
        throw new Error('No data returned from createCheckoutUrl');
      }
      window.location.href = data.createOrderVoucherCheckoutUrl.url;
    } catch (err) {
      toast.error(logAndFormatError(err, 'Error creating checkout URL.'));
    } finally {
      setIsRedirectingToCheckout(false);
    }
  }, [toast, createCheckoutUrl, i18n.language]);

  return (
    <div className="flex-1 space-y-8">
      <header>
        <div className="app-layout-wrapper space-y-4">
          <BackButton href={'/'} />
          <h1>{t('place_order.title', 'Order a Portugal NIF')}</h1>
        </div>
      </header>
      <main>
        <div className="app-layout-wrapper space-y-8">
          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0">
            <div className="basis-3/5 space-y-4 md:border-r md:border-gray-200 md:pr-8">
              <div className="flex items-center">
                <OrderVoucherIcon type="NIF" />
                <h2 className="ml-4">
                  {t('place_order.product', 'Portugal NIF')}
                </h2>
              </div>
              <p>
                {t(
                  'place_order.product_description',
                  'Get your Número de Identificação Fiscal (NIF), which is your taxpayer identification number in Portugal. Our service includes 1 year of fiscal representation.',
                )}
              </p>
            </div>

            <div className="basis-2/5 space-y-4 md:pl-8">
              <p className="tracking-tight text-gray-900">
                <span className="text-2xl">$150 </span>
                <span>{t('place_order.per_year', 'per year')}*</span>
              </p>

              <Button
                className="md:w-full"
                disabled={isRedirectingToCheckout}
                onClick={redirectToCheckout}
              >
                {t('place_order.payment_button', 'Continue to payment')}
              </Button>

              <p className="text-sm">
                *{' '}
                {t(
                  'place_order.price_note',
                  'You cancel the recurring charge after you become a Portugal tax resident and update your address with the tax office.',
                )}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <h4>
              {t('place_order.estimated_turnaround.header', 'Turnaround Time')}
            </h4>
            <IconBulletPointItem icon={HiOutlineClock}>
              <a
                href="https://bordr.com/turnaround"
                target="_blank"
                rel="noreferrer"
              >
                {t(
                  'place_order.estimated_turnaround.time',
                  'See current estimated turnaround time',
                )}
              </a>
            </IconBulletPointItem>
          </div>
          <div className="space-y-4">
            <h4>{t('place_order.process.header', 'Process')}</h4>
            <IconBulletPointItem icon={<NumberIcon number={1} />}>
              {t(
                'place_order.process.fill_intake',
                'Fill out the intake form to provide your information and documents',
              )}
            </IconBulletPointItem>
            <IconBulletPointItem icon={<NumberIcon number={2} />}>
              {t(
                'place_order.process.sign_power_of_attorney',
                'Sign your Power of Attorney',
              )}
            </IconBulletPointItem>
            <IconBulletPointItem icon={<NumberIcon number={3} />}>
              {t('place_order.process.receive_nif', 'Receive your NIF')}
            </IconBulletPointItem>
          </div>
          <div className="space-y-4">
            <h4>{t('place_order.documents.header', 'Required Documents')}</h4>
            <IconBulletPointItem icon={HiCheck}>
              <div className="space-y-2">
                <h5>
                  {t(
                    'place_order.documents.proof_of_identity',
                    'Proof of identity',
                  )}
                </h5>
                <p>
                  {t(
                    'place_order.documents.proof_of_identity_description',
                    "A high-quality image of the applicant's passport or EU National ID Card.",
                  )}
                </p>
              </div>
            </IconBulletPointItem>
            <IconBulletPointItem icon={HiCheck}>
              <div className="space-y-2">
                <h5>
                  {t(
                    'place_order.documents.proof_of_address',
                    'Proof of address',
                  )}
                </h5>
                <p>
                  {t(
                    'place_order.documents.proof_of_address_description',

                    "A bank statement, utility bill, or driver's license.",
                  )}
                </p>
              </div>
            </IconBulletPointItem>
            <p>
              <Trans
                t={t}
                i18nKey="place_order.documents.additional_requirements"
                defaults="See <button>additional requirements</button> for minors and Portuguese citizens."
                components={{
                  button: (
                    // eslint-disable-next-line jsx-a11y/control-has-associated-label
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={() => setShowAdditionalRequirementsModal(true)}
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>
      </main>
      <Modal
        isOpen={showAdditionalRequirementsModal}
        onClose={() => setShowAdditionalRequirementsModal(false)}
        size="x-large"
      >
        <div className="space-y-4">
          <div className="flex w-full items-start justify-between space-x-4">
            <h3>
              {t(
                'place_order.additional_requirements_modal.minor_title',
                'Additional Requirements for Minors (under the age of 18)',
              )}
              :
            </h3>
            <button
              type="button"
              onClick={() => setShowAdditionalRequirementsModal(false)}
            >
              <HiX className="h-6 w-6 text-gray-500" />
            </button>
          </div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              <Trans
                t={t}
                i18nKey="place_order.additional_requirements_modal.minor_birth_certificate"
                defaultValue="<strong>Minor's Birth Certificate</strong> - An Adoption Certificate is an acceptable substitute for a birth certificate."
              />
            </li>
            <li>
              <Trans
                t={t}
                i18nKey="place_order.additional_requirements_modal.minor_parents_passports"
                defaultValue="<strong>Parents' Passports</strong> - If the minor is being represented by a single parent, then only one parent's passport is required."
              />
            </li>
          </ul>
          <h3>
            {t(
              'place_order.additional_requirements_modal.portuguese_title',
              'Additional Requirements for Portuguese Citizens',
            )}
            :
          </h3>
          <ul className="list-inside list-disc space-y-4">
            <li>
              <Trans
                t={t}
                i18nKey="place_order.additional_requirements_modal.portuguese_birth_certificate"
                defaultValue="<strong>Portuguese Birth Certificate</strong> - If you do not have one, you can order one <a>here</a>."
                components={{
                  a: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      href="https://www.bordr.com/birth-certificate"
                      aria-label={t(
                        'prepare_documents.pt_citizenship.birth_certs_sr',
                        'Birth certificates at Bordr',
                      )}
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}

export default PlaceNifOrderPage;
